import React, { useMemo, useState, useEffect } from "react";
import "./OperationView.css";
import "../../management/PremiumGrid.css";
import { Vehicle, Depot, FleetOpsStatus } from "../../data/database";

// --- Props 타입 정의 ---
interface OperationViewProps {
  vehicles: Vehicle[];
}

// SoH(배터리 수명): ≥85 양호 · 80~85 주의 · <80 교체권고 — 본 제품(eview-ui)과 같은 기준
const SOH_BANDS = {
  green: { min: 85, color: "#16a34a" },
  orange: { min: 80, color: "#f97316" },
  red: { min: 0, color: "#dc2626" },
};
type Band = keyof typeof SOH_BANDS;

function bandOf(soh: number): Band {
  if (soh >= SOH_BANDS.green.min) return "green";
  if (soh >= SOH_BANDS.orange.min) return "orange";
  return "red";
}

// SoC(충전량) 색상: <30 낮음(빨강) · 30~80 적정(초록) · ≥80 높음(주황)
function socColor(soc: number): string {
  if (soc >= 80) return "#f97316"; // 높음 - 주황
  if (soc >= 30) return "#16a34a"; // 적정 - 초록
  return "#dc2626"; // 낮음 - 빨강
}

function socLabel(soc: number): string {
  if (soc >= 80) return "높음";
  if (soc >= 30) return "적정";
  return "낮음";
}

function sohLabel(soh: number): string {
  if (soh >= SOH_BANDS.green.min) return "양호";
  if (soh >= SOH_BANDS.orange.min) return "주의";
  return "교체권고";
}

const CURRENT_USER = "김민준";

type QuickFilter = "all" | "critical" | "warning" | "trending" | "my";

const QUICK_FILTERS: { key: QuickFilter; label: string }[] = [
  { key: "all", label: "전체" },
  { key: "critical", label: "교체권고 구간" },
  { key: "warning", label: "주의 구간" },
  { key: "trending", label: "최근 7일 급락" },
  { key: "my", label: "내 담당 차량" },
];

const statusLabel = (s: FleetOpsStatus) => {
  if (s === "Open") return "운행중";
  if (s === "In progress") return "충전중";
  return "수리 입고";
};

function ScoreRadar({ values, score }: { values: number[]; score: number }) {
  const size = 180;
  const cx = size / 2;
  const cy = size / 2;
  const radius = 70;
  const max = 100;
  const angleStep = (Math.PI * 2) / values.length;

  const points = values
    .map((v, i) => {
      const r = radius * (v / max);
      const angle = -Math.PI / 2 + angleStep * i;
      const x = cx + r * Math.cos(angle);
      const y = cy + r * Math.sin(angle);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  const framePoints = Array.from({ length: values.length })
    .map((_, i) => {
      const angle = -Math.PI / 2 + angleStep * i;
      const x = cx + radius * Math.cos(angle);
      const y = cy + radius * Math.sin(angle);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  // 항목명 수정
  const labels = ["전압", "온도", "용량", "불균형", "성능 감소율"];

  return (
    <svg width="100%" height={size} viewBox={`0 0 ${size} ${size}`}>
      <polygon points={framePoints} fill="none" stroke="#e5e7eb" strokeWidth={1} />
      {labels.map((_, i) => {
        const angle = -Math.PI / 2 + angleStep * i;
        const x = cx + radius * Math.cos(angle);
        const y = cy + radius * Math.sin(angle);
        return (
          <line
            key={`axis-${i}`}
            x1={cx}
            y1={cy}
            x2={x}
            y2={y}
            stroke="#e5e7eb"
            strokeWidth={1}
          />
        );
      })}
      <polygon
        points={points}
        fill="rgba(37, 99, 235, 0.15)"
        stroke="#2563eb"
        strokeWidth={2}
      />
      {labels.map((label, i) => {
        const angle = -Math.PI / 2 + angleStep * i;
        const r = radius + 16;
        const x = cx + r * Math.cos(angle);
        const y = cy + r * Math.sin(angle);
        return (
          <text
            key={`label-${i}`}
            x={x}
            y={y}
            fontSize={11}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#4b5563"
          >
            {label}
          </text>
        );
      })}
      <text
        x={cx}
        y={cy - 4}
        fontSize={20}
        textAnchor="middle"
        fill="#111827"
        fontWeight={700}
      >
        {score}점
      </text>
      <text x={cx} y={cy + 16} fontSize={11} textAnchor="middle" fill="#6b7280">
        배터리 점수
      </text>
    </svg>
  );
}

// -------- 메인 컴포넌트 --------
export default function OperationView({ vehicles }: OperationViewProps) {
  const [search, setSearch] = useState("");
  const [quick, setQuick] = useState<QuickFilter>("all");
  const [depot, setDepot] = useState<Depot | "all">("all");
  const [model] = useState<string | "all">("all");
  const [selected, setSelected] = useState<Vehicle | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [approvalEmail, setApprovalEmail] = useState("");
  const [disposalReason, setDisposalReason] = useState("배터리 성능 저하 (SoH 기준 미달)");
  const [disposalDetails, setDisposalDetails] = useState("");
  const [carType, setCarType] = useState("표준/저상 내장탑");


  const filtered = useMemo(() => {
    let rows = vehicles.filter((v) => v.obdStatus === "통신중");
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((r) => r.id.toLowerCase().includes(q));
    }
    if (depot !== "all") rows = rows.filter((r) => r.depot === depot);
    if (model !== "all") rows = rows.filter((r) => r.model === model);

    if (quick === "critical") rows = rows.filter((r) => bandOf(r.soh) === "red");
    else if (quick === "warning") rows = rows.filter((r) => bandOf(r.soh) === "orange");
    else if (quick === "trending") rows = rows.filter((r) => r.delta7d <= -3);
    else if (quick === "my") rows = rows.filter((r) => r.assignee === CURRENT_USER);

    rows.sort((a, b) => a.soh - b.soh);
    return rows;
  }, [search, quick, depot, model, vehicles]);

  useEffect(() => {
    setPage(0);
  }, [search, quick, depot, model, rowsPerPage]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const startIndex = page * rowsPerPage;
  const pageRows = filtered.slice(startIndex, startIndex + rowsPerPage);


  // 모듈 온도(예시) — 모두 정상, 30~40도 아래 구간으로 설정
  const moduleTemps = [
    { label: "모듈 1", value: 32 },
    { label: "모듈 2", value: 31 },
    { label: "모듈 3", value: 30 },
  ];

  // 0~40°C를 0~100%로 매핑해서 막대 길이로 사용
  const tempToWidth = (t: number) => {
    const min = 0;
    const max = 40;
    const clamped = Math.max(min, Math.min(max, t));
    return ((clamped - min) / (max - min)) * 100;
  };

  const isDrawerOpen = Boolean(selected);

  return (
    <div className="vm-page">
      <div className="soh-header-left" style={{ marginBottom: '24px' }}>
        <h2 className="soh-header-title">배터리 상태 모니터링</h2>
        <div className="soh-header-sub">차량별 배터리 충전량(SoC), 수명(SoH) 및 종합 점수를 실시간으로 확인합니다.</div>
      </div>
      {/* 통합 툴바 */}
      <div className="driving-toolbar" style={{ marginTop: '0', border: '1px solid #e2e8f0', borderBottom: 'none', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
        <div className="driving-toolbar-left" style={{ gap: '16px' }}>
          <div className="driving-section-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
              <path d="M2 17l10 5 10-5"></path>
              <path d="M2 12l10 5 10-5"></path>
            </svg>
            배터리 모니터링
            <button className="btn-refresh" title="새로고침" onClick={() => window.location.reload()}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <polyline points="1 20 1 14 7 14"></polyline>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                </svg>
            </button>
          </div>
          <div className="driving-tabs">
            {QUICK_FILTERS.map((f) => (
              <button
                key={f.key}
                className={`driving-tab-btn ${quick === f.key ? 'active' : ''}`}
                onClick={() => setQuick(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="driving-toolbar-right">
          <button className="driving-action-btn green-solid" onClick={() => alert("데이터 내보내기 기능은 추후 연동 예정입니다.")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="8" y1="13" x2="16" y2="13"></line>
                  <line x1="8" y1="17" x2="16" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              데이터 내보내기
          </button>
          
          <select
            className="vm-compact-select"
            value={depot}
            onChange={(e) => setDepot(e.target.value as Depot | "all")}
          >
            <option value="all">지점: 전체</option>
            {Array.from(new Set(vehicles.map((v) => v.depot)))
              .filter(Boolean)
              .map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
          </select>

          <div className="driving-search-wrapper">
              <input
                  type="text"
                  placeholder="차량 번호 검색… 예) 12가 1111"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="driving-search-input"
                  style={{ width: '220px' }}
              />
              <svg className="driving-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
          </div>
        </div>
      </div>

      {/* 메인 레이아웃 */}
      <div className="vm-main" style={{ marginTop: '0' }}>
        {/* 좌측 테이블 */}
        <div className="soh-table-card" style={{ borderTopLeftRadius: '0', borderTopRightRadius: '0' }}>
          <div className="soh-table-wrapper" style={{ overflowX: 'auto' }}>
            <table className="soh-table premium-grid driving-grid" style={{ minWidth: '950px' }}>
              <thead>
                <tr>
                  <th className="text-left">차량 <span className="sort-icon">▼</span></th>
                  <th style={{ width: '280px' }}>주행가능거리(충전량) <span className="sort-icon">▼</span></th>
                  <th style={{ width: '280px' }}>완충주행거리(배터리 수명) <span className="sort-icon">▼</span></th>
                  <th>마지막 진단 <span className="sort-icon">▼</span></th>
                  <th>운전자 <span className="sort-icon">▼</span></th>
                  <th>상태 <span className="sort-icon">▼</span></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((v) => {
                  const band = bandOf(v.soh);
                  const sohColor = SOH_BANDS[band].color;

                  const isSelected = selected?.id === v.id;

                  return (
                    <tr
                      key={v.id}
                      className={isSelected ? "soh-row-selected" : ""}
                      onClick={() => setSelected(v)}
                    >
                      <td className="text-left">
                        <div className="grid-icon-text">

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginLeft: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ color: '#0f172a', fontWeight: 600 }}>{v.id}</span>
                              {v.depot && (
                                <span style={{ fontSize: '11px', color: '#0284c7', backgroundColor: '#e0f2fe', padding: '1px 4px', borderRadius: '3px', fontWeight: 700 }}>{v.depot}</span>
                              )}
                            </div>
                            <span style={{ color: '#64748b', fontSize: '12px' }}>{v.model}</span>
                          </div>
                        </div>
                      </td>

                      {/* 주행가능거리(충전량) - SOC에 비례 (Mock) */}
                      <td>
                        <div className="soh-cell-soh-col" style={{ flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', minWidth: '80px', gap: '2px' }}>
                            <span style={{ fontSize: '15px', fontWeight: 600, color: socColor(v.soh) }}>
                              {Math.round(211 * (v.soh / 100))}
                            </span>
                            <span style={{ fontSize: '12px', color: '#94a3b8' }}>km <span style={{ color: '#cbd5e1' }}>/</span> 211km</span>
                          </div>

                          <div className="grid-bar-container" style={{ width: '100px', height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                            <div className="grid-bar-fill" style={{ width: `${v.soh}%`, height: '100%', backgroundColor: socColor(v.soh) }}></div>
                          </div>

                          <span style={{ fontSize: '14px', fontWeight: 600, color: '#334155', minWidth: '40px', textAlign: 'right' }}>
                            {Math.round(v.soh)}%
                          </span>
                        </div>
                      </td>

                      {/* 완충주행거리(배터리 수명) - SOH (Mock) */}
                      <td>
                        <div className="soh-cell-soh-col" style={{ flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', minWidth: '80px', gap: '2px' }}>
                            <span style={{ fontSize: '15px', fontWeight: 600, color: '#0ea5e9' }}>
                              {Math.round(211 * (v.batteryScore / 100))}
                            </span>
                            <span style={{ fontSize: '12px', color: '#94a3b8' }}>km <span style={{ color: '#cbd5e1' }}>/</span> 211km</span>
                          </div>

                          <div className="grid-bar-container" style={{ width: '100px', height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                            <div className="grid-bar-fill" style={{ width: `${v.batteryScore}%`, height: '100%', backgroundColor: '#67e8f9', opacity: 0.8 }}></div>
                          </div>

                          <span style={{ fontSize: '14px', fontWeight: 600, color: '#334155', minWidth: '40px', textAlign: 'right' }}>
                            {v.batteryScore}%
                          </span>
                        </div>
                      </td>

                      <td className="soh-last-seen" style={{ color: '#94a3b8' }}>-</td>

                      <td>
                        {v.assignee ? (
                          <div className="grid-icon-text">
                            <div className="grid-avatar gray" style={{ width: '24px', height: '24px' }}>{v.assignee.charAt(0)}</div>
                            <span>{v.assignee}</span>
                          </div>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>미지정</span>
                        )}
                      </td>

                      <td style={{ textAlign: 'center' }}>
                        <span
                          className={
                            `status-badge ${v.fleetOpsStatus === "Open"
                              ? "running"
                              : v.fleetOpsStatus === "In progress"
                                ? "charging"
                                : "stopped"
                            }`
                          }
                        >
                          {statusLabel(v.fleetOpsStatus)}
                        </span>
                      </td>
                    </tr>
                  );
                })}

                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="soh-empty">
                      조건에 해당하는 차량이 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 페이지네이션 (Advanced Pagination 적용) */}
          <div className="soh-pagination-advanced">
            <div className="pagination-controls">
              <button className="pag-btn pag-nav" disabled={page === 0} onClick={() => setPage(0)}>|&lt;</button>
              <button className="pag-btn pag-nav" disabled={page === 0} onClick={() => setPage(p => p - 1)}>&lt;</button>

              {Array.from({ length: Math.min(10, pageCount) }, (_, i) => {
                let startPage = Math.max(0, Math.min(page - 4, pageCount - 10));
                let pageNum = startPage + i;
                if (pageNum >= pageCount) return null;
                return (
                  <button
                    key={pageNum}
                    className={`pag-btn ${page === pageNum ? 'active' : ''}`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum + 1}
                  </button>
                 );
              })}

              <button className="pag-btn pag-nav" disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>&gt;</button>
              <button className="pag-btn pag-nav" disabled={page >= pageCount - 1} onClick={() => setPage(pageCount - 1)}>&gt;|</button>
            </div>
            <div className="pagination-info">
              <span>{page + 1}/{pageCount} 페이지({filtered.length} 개)</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>페이지 당 행 수:</span>
                <select className="soh-select" style={{ padding: '4px 8px' }} value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* 우측 폼 패널 오버레이 (Drawer) */}
        <div className={`vm-backdrop ${isDrawerOpen ? "open" : ""}`} onClick={() => setSelected(null)}></div>
        <aside className={`vm-form-container ${isDrawerOpen ? "open" : ""}`}>
          {selected && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <div>
                  <h3 className="vm-form-title" style={{ margin: 0, fontSize: '16px', color: '#111827' }}>선택된 차량</h3>
                  <p className="soh-detail-sub" style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                    {selected.id} · {selected.model} · {selected.depot} 지점 · {selected.assignee || '미지정'}
                  </p>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}
                >
                  &times;
                </button>
              </div>

              <div className="soh-detail-grid" style={{ marginTop: '20px' }}>
                {/* 배터리 초기 대비 용량 */}
                <div className="soh-detail-card" style={{ padding: '24px' }}>
                  <div className="soh-detail-label" style={{ marginBottom: '16px', fontSize: '14px', color: '#4b5563' }}>배터리 초기 대비 용량 (SOH)</div>

                  {(() => {
                    const band = bandOf(selected.soh);
                    const color = SOH_BANDS[band].color;
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <span style={{ fontSize: '32px', fontWeight: 700, color: color, minWidth: '80px' }}>
                          {Math.round(selected.soh)}%
                        </span>

                        <div style={{ flex: 1, height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ width: `${selected.soh}%`, height: '100%', backgroundColor: color }} />
                        </div>

                        <span style={{ fontSize: '14px', fontWeight: 600, color: color, minWidth: '60px', textAlign: 'right' }}>
                          {sohLabel(selected.soh)}
                        </span>
                      </div>
                    );
                  })()}
                </div>

                {/* 배터리 점수 레이더 */}
                <div className="soh-detail-card">
                  <div className="soh-detail-label">배터리 점수</div>
                  <ScoreRadar values={selected.scoreRadar} score={selected.batteryScore} />
                </div>

                {/* 셀 전압 편차 */}
                <div className="soh-detail-card">
                  <div className="soh-detail-label">셀 전압 편차</div>
                  <div className="soh-detail-soh-row">
                    <span className="soh-detail-main">0.01V</span>
                    <span className="soh-detail-soh-sub">
                      <span className="soh-status-chip">
                        <span className="soh-status-dot normal" />
                        정상
                      </span>
                    </span>
                  </div>
                </div>

                {/* 모듈 온도 (고도화 버전) */}
                <div className="soh-detail-card temp-card">
                  <div className="temp-card-header">
                    <div className="soh-detail-label">모듈 온도</div>
                    <span className="temp-range-chip">정상 범위 0–40°C</span>
                  </div>
                  <div className="temp-rows">
                    {moduleTemps.map((t) => (
                      <div key={t.label} className="temp-row">
                        <div className="temp-label">{t.label}</div>
                        <div className="temp-bar-wrap">
                          <div className="temp-bar-bg">
                            <div
                              className="temp-bar-fill"
                              style={{ width: `${tempToWidth(t.value)}%` }}
                            />
                          </div>
                        </div>
                        <div className="temp-value-wrap">
                          <span className="temp-value">
                            {t.value}
                            <span className="temp-unit">°C</span>
                          </span>
                          <span className="soh-status-chip">
                            <span className="soh-status-dot normal" />
                            정상
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ✅ 처분 심사 요청 액션 버튼 */}
                <div className="soh-maintenance-action" style={{ paddingBottom: '20px', display: 'flex', gap: '8px' }}>
                  <button
                    className="soh-btn"
                    onClick={() => setIsApprovalModalOpen(true)}
                    style={{ flex: 1, padding: '12px', backgroundColor: '#f59e0b', color: '#fff', border: 'none' }}>
                    차량 처분 심사 요청
                  </button>
                </div>
              </div>
            </>
          )}
        </aside>

        {/* 결재권자 승인 요청 모달 */}
        {isApprovalModalOpen && selected && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '400px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#0f172a' }}>승인 요청</h3>
                <button onClick={() => setIsApprovalModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}>&times;</button>
              </div>
              <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '16px' }}>
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22 6 12 13 2 6"></polyline>
                </svg>
                <p style={{ fontSize: '15px', color: '#1e293b', marginBottom: '8px', fontWeight: 500 }}>
                  결재권자에게 해당 차량 ({selected.model}, {selected.id})의<br />
                  처분 심사 승인을 요청하시겠습니까?
                </p>
                <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                  결재권자가 승인하면 자산 처분 심사 목록으로 이동됩니다.
                </p>
                <div style={{ textAlign: 'left', marginTop: '20px', backgroundColor: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>결재권자 이메일 주소 (필수)</label>
                  <input
                    type="email"
                    placeholder="approver@example.com"
                    value={approvalEmail}
                    onChange={(e) => setApprovalEmail(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', color: '#0f172a', marginBottom: '16px', boxSizing: 'border-box' }}
                  />

                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>주요 처분 사유 및 제원 특징</label>
                  <select 
                    value={disposalReason}
                    onChange={(e) => {
                      setDisposalReason(e.target.value);
                      if (e.target.value !== "기타 사유 (직접 입력)") setDisposalDetails("");
                    }}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', color: '#0f172a', marginBottom: '8px', boxSizing: 'border-box' }}
                  >
                      <option value="배터리 성능 저하 (SoH 기준 미달)">배터리 성능 저하 (SoH 기준 미달)</option>
                      <option value="내용연수 도과 (노후 차량)">내용연수 도과 (노후 차량)</option>
                      <option value="사고로 인한 치명적 손상 (전손)">사고로 인한 치명적 손상 (전손)</option>
                      <option value="기타 사유 (직접 입력)">기타 사유 (직접 입력)</option>
                  </select>
                  {disposalReason === "기타 사유 (직접 입력)" && (
                    <textarea 
                      placeholder="기타 사유 및 상세 제원 특징을 입력하세요." 
                      value={disposalDetails}
                      onChange={(e) => setDisposalDetails(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', minHeight: '60px', resize: 'vertical', boxSizing: 'border-box', marginTop: '4px' }}
                    />
                  )}

                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '8px', marginTop: '16px' }}>차량 유형 선택</label>
                  <select 
                    value={carType}
                    onChange={(e) => setCarType(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', color: '#0f172a', marginBottom: '8px', boxSizing: 'border-box' }}
                  >
                      <option value="카고">카고</option>
                      <option value="표준/저상 내장탑">표준/저상 내장탑</option>
                      <option value="하이 내장탑">하이 내장탑</option>
                      <option value="냉동탑차">냉동탑차</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', padding: '16px 20px', borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                <button style={{ flex: 1, padding: '10px', backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 500, color: '#475569' }} onClick={() => setIsApprovalModalOpen(false)}>취소</button>
                <button 
                  style={{ flex: 1, padding: '10px', backgroundColor: '#3b82f6', border: '1px solid #3b82f6', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 500, color: 'white' }}
                  onClick={() => {
                    if (!approvalEmail.trim()) {
                      alert("결재권자 이메일을 입력해주세요.");
                      return;
                    }
                    if (disposalReason === "기타 사유 (직접 입력)" && !disposalDetails.trim()) {
                      alert("기타 사유 내용을 자세히 작성해주세요.");
                      return;
                    }
                    alert(`[${approvalEmail}] 로 승인 요청 발송이 무사히 완료되었습니다!\n\n[데모 목적 가이드]\n결재권자가 이메일을 통해 처분 심사를 승인하여, 해당 차량이 '자산처분 승인 차량' 풀(Pool)로 자동 편입되었습니다.\n*선택 사유: ${disposalReason}\n*차량 유형: ${carType}`);
                    
                    const approved = JSON.parse(localStorage.getItem('disposal_approvals') || '[]');

                    const purchaseYear = parseInt((selected.purchaseDate || "2023").substring(0, 4));
                    const ageYears = Math.max(0, 2026 - purchaseYear);
                    const mileageDepreciation = 0.0025 * selected.mileage; // 주행 감가: 10000km당 25만원 (상용차 특성 반영 완화)
                    const sohDeduction = 15 * Math.max(0, 100 - selected.soh); // SoH 미달 패널티 (1%당 15만)
                    
                    let optionBonus = 0;
                    if (carType.includes('카고')) optionBonus = 0;
                    else if (carType.includes('표준') || carType.includes('저상')) optionBonus = 250; 
                    else if (carType.includes('하이')) optionBonus = 300; 
                    else if (carType.includes('냉동')) optionBonus = 450; 

                    // 현실적 딜러 매입가 산출 공식: (시장 기준가 2300 + 딜러매입옵션가) * 연식 감가(연 8.5% 정률) - 주행/배터리 감가
                    const basePrice = 2300; 
                    const depreciatedBase = (basePrice + optionBonus) * Math.pow(0.915, ageYears);

                    const calculatedValueManWon = depreciatedBase - mileageDepreciation - sohDeduction;
                    const usedCarValueActual = Math.max(0, Math.floor(calculatedValueManWon) * 10000);

                    approved.push({
                      id: selected.id,
                      model: selected.model,
                      depot: selected.depot || '전국',
                      driver: selected.assignee || '미지정',
                      saleStatus: '자산처분 승인',
                      estimatedValue: usedCarValueActual,
                      usedCarValue: usedCarValueActual,
                      remanCost: 4500000,
                      soh: selected.soh,
                      accidentHistory: false,
                      phone: "010-0000-0000",
                      carType: carType
                    });
                    localStorage.setItem('disposal_approvals', JSON.stringify(approved));
                    
                    setIsApprovalModalOpen(false);
                    setApprovalEmail("");
                    setDisposalDetails("");
                    setCarType("표준/저상 내장탑");
                    setSelected(null);
                  }}
                >
                  승인 요청
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
